import React, { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  PackagePlus,
  Undo2,
  Boxes,
  Layers3,
  Truck,
  LogOut,
  ChevronDown,
  Settings,
  User as UserIcon,
  Calculator,
  Package,
} from "lucide-react";

// Cada item do menu declara a permissão necessária.
// Admin master vê tudo.
const NAV = [
  { to: "/", label: "Painel", icon: LayoutDashboard, testid: "nav-painel", end: true, perm: "painel:view" },
  { to: "/entrada", label: "Entrada", icon: PackagePlus, testid: "nav-entrada", perms: ["entrada:individual", "entrada:lote", "entrada:ver_docs"] },
  { to: "/romaneio", label: "Romaneio", icon: Truck, testid: "nav-romaneio", perm: "romaneio:view" },
  { to: "/devolucao", label: "Devolução", icon: Undo2, testid: "nav-devolucao", perm: "devolucao:executar" },
  { to: "/rolos", label: "Estoque", icon: Layers3, testid: "nav-rolos", perm: "estoque:view" },
  { to: "/gola-punho", label: "Gola e Punho", icon: Package, testid: "nav-gola-punho", perm: "estoque_gp:view" },
  { to: "/cadastro", label: "Cadastro", icon: Boxes, testid: "nav-cadastro", perms: ["cadastro:fornecedores_view", "cadastro:combinacoes_view"] },
  { to: "/calculadora", label: "Calculadora", icon: Calculator, testid: "nav-calculadora", perm: "calculadora:view" },
];

export default function Layout() {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Guard: se o user precisa trocar a senha (provisória), força ir para /trocar-senha
  useEffect(() => {
    if (!user || user === false) return;
    if (user.must_change_password && location.pathname !== "/trocar-senha") {
      navigate("/trocar-senha", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleNav = NAV.filter((item) => {
    if (user?.is_master) return true;
    if (item.perm) return hasPerm(item.perm);
    if (item.perms) return item.perms.some((p) => hasPerm(p));
    return false;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b-2 border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="brand-home"
            className="flex items-center gap-3 rounded-md text-left transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            aria-label="Ir para a página inicial"
          >
            <img
              src="/logo-wb.png"
              alt="West Brasil"
              className="h-10 w-10 rounded-md object-cover"
              data-testid="brand-logo"
            />
            <div className="leading-tight">
              <div className="text-base font-bold tracking-tight text-slate-900">WEST BRASIL</div>
              <div className="text-xs font-medium text-slate-500">Estoque de Tecido</div>
            </div>
          </button>

          <div className="flex items-center gap-3">
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="account-menu-trigger"
                className="flex items-center gap-2 rounded-md border-2 border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
              >
                <UserIcon size={16} />
                <span className="hidden sm:inline">{user?.name || "Conta"}</span>
                <ChevronDown size={14} className={menuOpen ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-md border-2 border-slate-200 bg-white shadow-lg"
                  data-testid="account-menu"
                >
                  <div className="border-b border-slate-100 px-3 py-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {user?.is_master ? "Administrador" : "Sua conta"}
                    </div>
                    <div className="text-sm font-medium text-slate-700">{user?.name}</div>
                    <div className="text-xs text-slate-400">{user?.email}</div>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); navigate("/configuracoes"); }}
                    data-testid="account-menu-configuracoes"
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <Settings size={16} /> Configurações
                  </button>
                  <button
                    onClick={handleLogout}
                    data-testid="account-menu-logout"
                    className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2.5 text-left text-sm font-semibold text-red-600 hover:bg-red-50"
                  >
                    <LogOut size={16} /> Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {visibleNav.length > 0 && (
          <nav className="mx-auto max-w-6xl overflow-x-auto px-2">
            <div className="flex min-w-max gap-1 pb-2">
              {visibleNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                >
                  <item.icon size={18} /> {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}
